import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Phone, ExternalLink, Shield, Building2 } from 'lucide-react';

// Datos corporativos reales de Leapifyes / DigitalLeap Solutions S.L.
const corporateInfo = {
  productName: "MANDO",
  tagline: "by Leapifyes",
  companyName: "DigitalLeap Solutions S.L.",
  cif: "B22984454",
  location: "Barcelona, España",
  email: "info@leapifyes.com",
  phone: "+34 694 214 849",
  website: "https://www.leapifyes.com",
  legalUrls: {
    avisoLegal: "https://www.leapifyes.com/aviso-legal",
    privacidad: "https://www.leapifyes.com/politica-de-privacidad",
    cookies: "https://www.leapifyes.com/politica-de-cookies",
  }
};

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    producto: [
      { label: 'Funcionalidades', to: '/funcionalidades' },
      { label: 'Precios', to: '/precios' },
      { label: 'IA en MANDO', to: '/inteligencia-artificial' },
      { label: 'Para quién', to: '/para-quien' },
    ],
    empresa: [
      { label: 'Blog', to: '/blog' },
      { label: 'Contacto', to: '/contacto' },
      { label: 'Leapifyes', href: corporateInfo.website, external: true },
    ],
    legal: [
      { label: 'Aviso Legal', href: corporateInfo.legalUrls.avisoLegal, external: true },
      { label: 'Política de Privacidad', href: corporateInfo.legalUrls.privacidad, external: true },
      { label: 'Política de Cookies', href: corporateInfo.legalUrls.cookies, external: true },
    ],
  };

  return (
    <footer className="bg-slate-900 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-4 lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="https://res.cloudinary.com/dnjsjo6yx/image/upload/w_80,h_80,c_fill,f_webp,q_auto/v1770925218/MANDO_by_Leapifyes_tajoao"
                alt="MANDO by Leapifyes"
                className="h-10 w-auto"
                width={40}
                height={40}
                loading="lazy"
              />
            </div>

            {/* Trust Badge */}
            <div className="flex items-center gap-2 mb-4 text-sm">
              <Shield className="w-4 h-4 text-orange-500" />
              <span className="text-slate-400">
                Un producto de <a href={corporateInfo.website} target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:text-orange-300 transition-colors underline decoration-orange-400/50">Leapifyes</a>
              </span>
            </div>

            <p className="text-slate-400 mb-6 max-w-sm text-sm">
              Especialistas en transformación digital para pymes en España. MANDO es el sistema de gestión diseñado específicamente para empresas de reformas, mantenimiento y servicios del hogar.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <MapPin className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <span>{corporateInfo.location}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <Mail className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <a href={`mailto:${corporateInfo.email}`} className="hover:text-white transition-colors">
                  {corporateInfo.email}
                </a>
              </div>
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <Phone className="w-4 h-4 text-orange-500 flex-shrink-0" />
                <a href={`tel:${corporateInfo.phone}`} className="hover:text-white transition-colors">
                  {corporateInfo.phone}
                </a>
              </div>
            </div>
          </div>

          {/* Links Columns */}
          <div>
            <h3 className="text-white font-semibold mb-4">Producto</h3>
            <ul className="space-y-3">
              {footerLinks.producto.map((link) => (
                <li key={link.label}>
                  <Link to={link.to} className="text-slate-400 hover:text-white transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">Empresa</h3>
            <ul className="space-y-3">
              {footerLinks.empresa.map((link) => (
                <li key={link.label}>
                  {link.external ? (
                    <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-1">
                      {link.label} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <Link to={link.to} className="text-slate-400 hover:text-white transition-colors text-sm">
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-white transition-colors text-sm flex items-center gap-1"
                  >
                    {link.label}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Corporate Legal Section */}
        <div className="py-6 border-t border-slate-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Company Legal Info */}
            <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-slate-500" />
                <div className="text-sm">
                  <span className="text-slate-400">MANDO · by Leapifyes es desarrollado y operado por </span>
                  <span className="text-white font-medium">{corporateInfo.companyName}</span>
                </div>
              </div>
              <div className="text-slate-400 text-xs hidden md:block">|</div>
              <div className="text-slate-400 text-xs">
                CIF: {corporateInfo.cif}
              </div>
            </div>

            {/* Leapifyes Logo Link */}
            <a
              href={corporateInfo.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              aria-label="Visita Leapifyes.com"
            >
              <img
                src="https://res.cloudinary.com/dnjsjo6yx/image/upload/w_64,h_64,c_fill,f_webp,q_auto/v1770925218/MANDO_by_Leapifyes_tajoao"
                alt="Leapifyes"
                className="h-8 w-8 rounded-full object-cover"
                width={32}
                height={32}
                loading="lazy"
              />
              <span className="text-sm">leapifyes.com</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="py-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-400 text-xs">
            © {currentYear} {corporateInfo.companyName}. Todos los derechos reservados.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-3">
            <a
              href="https://www.linkedin.com/company/leapifyes"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-orange-500 hover:text-white transition-all duration-300"
              aria-label="LinkedIn Leapifyes"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
            </a>
            <a
              href="https://wa.me/34694214849"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-[#25D366] hover:text-white transition-all duration-300"
              aria-label="WhatsApp MANDO"
            >
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" /></svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
